/**
 * 🌓 **테마(라이트/다크) 한 벌.** 이 파일이 «무엇이 다크인가» 의 단일 출처다.
 *
 * ## 왜 모듈 하나인가
 *
 * 이 레포는 같은 코드를 다섯 앱에 복사해 뒀다가 값을 치른 적이 있다
 * (`safe-storage.ts` 머리말의 `ENTER-FUN-STREAMER-C` — iOS 에서 `localStorage` 가
 * `null` 이라 다섯 앱이 똑같이 죽었다). 테마도 정확히 같은 모양이다 — 저장 키·클래스
 * 이름·시스템 감지가 앱마다 흩어지면, 한 앱만 고친 뒤 「고쳤다」고 말하게 된다.
 *
 * ## 🔴 FOUC — 초기화는 번들보다 «먼저» 돌아야 한다
 *
 * React 가 마운트된 뒤에 클래스를 붙이면 그 전까지 **라이트 화면이 한 번 번쩍인다.**
 * 그래서 `<head>` 안에서 동기로 도는 인라인 스크립트가 필요하고, 그 스크립트는
 * 번들을 import 할 수 없다(번들보다 먼저 돈다). 즉 **정의가 두 곳에 산다.**
 *
 * 이 레포는 「정의를 바꾸면 그 정의가 «박힌» 자리를 전부 센 다음에 고쳐라」를 여러 번
 * 값으로 치렀다. 그래서 인라인 스크립트를 **여기서 생성**하고
 * ({@link THEME_INIT_SCRIPT}), `tests/theme-wiring.test.ts` 가 **모든 진입점의
 * 스크립트가 이 문자열과 같은지** 값으로 고정한다. 한쪽만 고치면 그 검사가 빨개진다.
 *
 * ## ⚠️ `localStorage` 는 없을 수도, 던질 수도 있다
 *
 * 저장은 {@link safeGet}/{@link safeSet} 을 쓴다(`safe-storage.ts`). 인라인 스크립트도
 * 같은 두 실패 모양을 `try` 로 막는다 — 거기서 던지면 **앱이 뜨기도 전에 죽는다.**
 */
import { safeGet, safeSet } from './safe-storage';

/**
 * 사용자가 고를 수 있는 값. `system` 은 «OS 를 따른다» 이고, 그것이 기본이다 —
 * 폰이 밤에 다크로 바뀌면 우리 화면도 같이 바뀌는 것이 사용자가 기대하는 동작이다.
 */
export type Theme = 'light' | 'dark' | 'system';

/** 실제로 화면에 적용되는 두 값. `system` 은 여기로 «해석» 된다. */
export type ResolvedTheme = 'light' | 'dark';

/**
 * 저장 키. 🔴 바꾸면 인라인 스크립트도 같이 바꿔야 한다(검사가 잡는다).
 * 앱마다 다른 키를 쓰지 않는다 — 같은 기기에서 앱을 오가며 테마가 튀면 고장으로 보인다.
 */
export const THEME_STORAGE_KEY = 'yoy-theme';

/** `<html>` 에 붙는 클래스. Tailwind 의 `darkMode: 'class'` 가 이 이름을 본다. */
export const DARK_CLASS = 'dark';

/** 시스템 다크 여부를 묻는 미디어 쿼리. 두 곳(여기·인라인)이 같은 문자열을 쓴다. */
export const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

/** 열거 밖을 조용히 받아들이지 않는다 — 저장소가 오염돼도 알려진 값으로만 떨어진다. */
export function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * 시스템이 다크인가. SSR·`matchMedia` 없는 환경에서는 **라이트로 떨어진다** —
 * 「모른다」를 다크로 읽으면 서버 렌더와 클라이언트가 어긋나 hydration 이 깨진다.
 */
export function systemPrefersDark(): boolean {
  try {
    return globalThis.matchMedia?.(DARK_MEDIA_QUERY).matches ?? false;
  } catch {
    return false;
  }
}

/** `system` 을 실제 두 값 중 하나로 해석한다. */
export function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return theme;
}

/**
 * 저장된 선택을 읽는다. 없거나 깨졌으면 `system`.
 *
 * ⚠️ 「저장소를 못 읽었다」와 「고른 적 없다」를 **구별하지 않는다.** 둘 다 「OS 를
 *    따른다」가 올바른 동작이고, 그게 크래시보다 낫다(`safe-storage.ts` 와 같은 판단).
 */
export function readTheme(): Theme {
  const raw = safeGet(THEME_STORAGE_KEY);
  return isTheme(raw) ? raw : 'system';
}

/** 선택을 저장한다. 저장이 실패해도 «이번 세션» 의 적용은 그대로 유효하다. */
export function writeTheme(theme: Theme): boolean {
  return safeSet(THEME_STORAGE_KEY, theme);
}

/**
 * DOM 에 실제로 적용한다.
 *
 * 두 가지를 한다:
 * 1. `<html>` 의 `dark` 클래스 — Tailwind 유틸리티가 이것을 본다
 * 2. `color-scheme` — **이게 빠지면 스크롤바·폼 컨트롤·`<select>` 드롭다운이 흰색으로
 *    남는다.** 브라우저 기본 UI 는 Tailwind 가 못 칠한다. 다크 화면에 흰 스크롤바가
 *    나는 것은 「다크 지원」이라고 부를 수 없다.
 */
export function applyTheme(theme: Theme): ResolvedTheme {
  const resolved = resolveTheme(theme);
  const root = globalThis.document?.documentElement;
  if (!root) return resolved;
  root.classList.toggle(DARK_CLASS, resolved === 'dark');
  root.style.colorScheme = resolved;
  // 선택값 자체도 남긴다 — 「system 인데 지금 다크」와 「dark 로 고정」을 CSS·E2E 가
  // 구별할 수 있어야 한다(클래스만으로는 둘이 같은 모양이다).
  root.dataset.theme = theme;
  return resolved;
}

/**
 * OS 테마 변경을 구독한다. 선택이 `system` 일 때만 의미가 있다.
 * 해제 함수를 돌려준다 — **반드시 떼라**(안 떼면 리스너가 쌓인다).
 */
export function subscribeSystemTheme(onChange: (prefersDark: boolean) => void): () => void {
  let mql: MediaQueryList | undefined;
  try {
    mql = globalThis.matchMedia?.(DARK_MEDIA_QUERY);
  } catch {
    return () => {};
  }
  if (!mql) return () => {};
  const handler = (e: MediaQueryListEvent) => onChange(e.matches);
  // Safari 14 미만은 addEventListener 가 없다 — 구형에서 던지면 앱이 죽는다.
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }
  mql.addListener(handler);
  return () => mql.removeListener(handler);
}

/**
 * 🔴 **`<head>` 에서 동기로 도는 FOUC 방지 스크립트.**
 *
 * 번들보다 먼저 돌아야 하므로 이 모듈을 import 할 수 없다. 그래서 **문자열로 생성**하고
 * 각 앱의 진입점이 그대로 심는다. 값은 전부 위 상수에서 보간하므로, 키·클래스를 바꾸면
 * 스크립트도 자동으로 따라간다 — 손으로 두 번 적지 않는다.
 *
 * ⚠️ **절대 던지지 않는다.** 여기서 예외가 나면 그 뒤의 모든 스크립트가 멈추고 앱이
 *    흰 화면이 된다. `localStorage` 의 두 실패 모양(① null ② 접근이 던진다)을 `try`
 *    하나로 덮는다.
 *
 * @param defaultTheme 저장된 선택이 없을 때 쓸 값. 에디터처럼 다크가 기본인 앱이 있다.
 */
export function buildThemeInitScript(defaultTheme: Theme = 'system'): string {
  // 한 줄로 유지한다 — HTML 에 심을 때 들여쓰기가 섞이면 검사의 동일성 비교가 서식에
  // 의존하게 된다(이 레포가 「순서·서식에 의존하는 검사」로 세 번 값을 치렀다).
  return (
    `(function(){try{` +
    `var d=document.documentElement;` +
    `var s=null;try{s=window.localStorage&&window.localStorage.getItem("${THEME_STORAGE_KEY}")}catch(e){}` +
    `var t=(s==="light"||s==="dark"||s==="system")?s:"${defaultTheme}";` +
    `var m=false;try{m=window.matchMedia&&window.matchMedia("${DARK_MEDIA_QUERY}").matches}catch(e){}` +
    `var r=t==="system"?(m?"dark":"light"):t;` +
    `d.classList.toggle("${DARK_CLASS}",r==="dark");` +
    `d.style.colorScheme=r;` +
    `d.dataset.theme=t;` +
    `}catch(e){}})();`
  );
}

/** 기본(`system`) 스크립트. 앱 다섯이 이것을 그대로 쓴다. */
export const THEME_INIT_SCRIPT = buildThemeInitScript('system');
